import React, { useCallback, useContext, useEffect } from "react";
import EditorViews from "../components/editors/EditorViews.jsx";
import BryntumContext, { BryntumContextProvider, getSetting, userPreferences } from "../context/bryntumContext.jsx";
import AdminMainRoutes from "../routes/AdminMainRoutes.jsx";
import AuthContext from "../context/authContext.jsx";
import AppHeader from "../components/appHeader/AppHeader.jsx";
import PreviewPage from "../pages/PreviewPage.jsx";
import { SWIVU_HOME_VIEW } from "../../other/constants/OtherKeys.jsx";
import useDisplayHomeModal from "../hooks/useDisplayHomeModal.jsx";
import { getSearchParam, showToast } from "../../other/utils/utils.jsx";
import { updateURLQueryParam } from "../../other/utils/urlUtils.jsx";
import { SwivuDevTools } from "../../../swivuDevTool.jsx";
import { Toaster } from "@/modern/components/ui/sonner";
import { OpenAIKeyModalProvider } from "@/integrations";
import { EntityManagerView } from "../components/entityManager/EntityManagerView";
import { CacheBuster } from "../components/CacheBuster";

const intervalTimeInSec = 1800 * 1000;

let intervalId = null;
const { SWIVU_ENV } = import.meta.env;

const AdminLayout = () => {
  const user = useContext(AuthContext);
  const bryntumContext = useContext(BryntumContext);
  const authContext = useContext(AuthContext);
  const { isAppLoaded } = bryntumContext;
  const { setDisplayAuthForm, displayAuthForm } = authContext;
  useDisplayHomeModal();
  const applyFontWeight = useCallback((isApply) => {
    if (isApply) document.body.classList.add("swivu-font-weight");
    else document.body.classList.remove("swivu-font-weight");
  }, []);
  useEffect(() => {
    if (isAppLoaded && userPreferences) {
      applyFontWeight(getSetting("fontType") === "Heavy");
      userPreferences.on("change", ({ record }) => {
        if (record?.key === "fontType") {
          applyFontWeight(record.value === "Heavy");
        }
      });
    }
  }, [applyFontWeight, isAppLoaded]);

  useEffect(() => {
    if (user?.isDemoUser && !displayAuthForm) {
      if (!intervalId) {
        intervalId = setInterval(() => {
          setDisplayAuthForm({ type: SWIVU_HOME_VIEW.auth });
        }, intervalTimeInSec);
      }
    }
    return () => {
      if (isAppLoaded && user?.isDemoUser) {
        clearInterval(intervalId);
      }
    };
  }, [displayAuthForm, user?.isDemoUser, setDisplayAuthForm, isAppLoaded]);

  // This is here because of when logged user add params manually in url
  // than on logout modal not showing because of params in url.
  useEffect(() => {
    if (isAppLoaded && user.initialized && user.user && getSearchParam("guest-login")) {
      const url = new URL(window.location.href);
      url.searchParams.delete("guest-login");
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }
  }, [isAppLoaded, user.user, user.initialized]);

  useEffect(() => {
    if (!isAppLoaded || !user.initialized || !user.user) return;
    const domainStatus = getSearchParam("domain_status");
    if (!domainStatus) return;

    const messages = {
      active: { msg: "Custom domain is now active.", type: "success" },
      provisioning: { msg: "Domain verified — provisioning in progress.", type: "success" },
      pending_verification: { msg: "Finish DNS setup to activate your custom domain.", type: "info" },
      failed: { msg: "Custom domain setup failed. Open Go live to retry.", type: "error" },
      unset: { msg: "Custom domain status updated.", type: "info" },
    };
    const toastConfig = messages[domainStatus] ?? { msg: "Custom domain status updated.", type: "info" };
    showToast({ ...toastConfig, duration: 5000 });

    if (bryntumContext?.project?.load) {
      void bryntumContext.project.load();
    }

    updateURLQueryParam("domain_status", "", true);
  }, [isAppLoaded, user.initialized, user.user, bryntumContext?.project]);

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {user.initialized && user.user && <AppHeader />}
      {isAppLoaded && (
        <>
          <AdminMainRoutes />
          <EntityManagerView />
        </>
      )}
      {user.initialized && !user.user && <PreviewPage />}
      {isAppLoaded && <EditorViews />}
      {/* This is a utility dev tool component and can be removed from here */}
      {SWIVU_ENV !== "production" && (
        <SwivuDevTools
          data={{
            bryntumContext,
            authContext
          }}
        />
      )}
      <CacheBuster isAppLoaded={isAppLoaded} />
    </div>
  );
};

const AdminLayoutWrapper = () => (
  <BryntumContextProvider>
    <OpenAIKeyModalProvider>
      <AdminLayout />
      <Toaster />
    </OpenAIKeyModalProvider>
  </BryntumContextProvider>
);

export default AdminLayoutWrapper;
